import { defineComponent as _defineComponent } from 'vue'
import { computed, defineAsyncComponent } from 'vue';

import { MonetizationType, PresentationType, ReleaseType } from '@/@types/graphql-types';

import { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { TitleDetail, TitleDetailSeason } from '@/interfaces/title-details-graphql';
import AdUnit from '../AdUnit.vue';
import UpcomingSpinningSegment from '@/components/details/spinning-text/UpcomingSpinningSegment.vue';
import { formattedReleaseDate } from '@/helpers/date-helper';
import {
	allowedLanguagesUpcomingReleases,
	getUpcomingLabelForFormattedDate,
} from '@/features/buybox/helpers/upcoming-helpers';
import { UpcomingRelease } from '@/features/buybox/types/types';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { AVOD_TECHNICAL_NAMES, useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import { getOffersForCountry } from '@/helpers/offer-helper';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import Provider from '@/interfaces/provider';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';


export default /*#__PURE__*/_defineComponent({
  __name: 'SpinningText',
  props: {
    title: null,
    showRecentEpisodeList: { type: Boolean },
    hideSpinningText: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail; showRecentEpisodeList: boolean; hideSpinningText?: boolean };

const DvdBluRay = defineAsyncComponent(() => import('@/components/details/spinning-text/DvdBluray.vue'));
const Cinema = defineAsyncComponent(() => import('@/components/details/spinning-text/Cinema.vue'));
const FreeV2 = defineAsyncComponent(() => import('@/components/details/spinning-text/FreeV2.vue'));
const Upcoming = defineAsyncComponent(() => import('@/components/details/spinning-text/Upcoming.vue'));



const { country, language } = storeToRefs(useLanguageStore());
const { allProvidersByShortName } = storeToRefs(useConstantStore());

const { allRegularOffers, isInternationalOffersActive, AVODOffer } = useBuyBoxData({
	title: () => props.title,
	buyboxData: null,
	buyboxFilter: BuyBoxFilterOptions.ALL,
});

const avodExcludedTechnicalNames = computed(() =>
	AVODOffer.value ? AVOD_TECHNICAL_NAMES.ACTIVE : AVOD_TECHNICAL_NAMES.INACTIVE
);

const domesticOffers = computed(() => getOffersForCountry(props.title.offers, country.value));

const filteredOffers = computed(() =>
	domesticOffers.value.filter(
		offer =>
			![PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
				offer.presentationType
			) && !avodExcludedTechnicalNames.value.includes(offer.package.technicalName)
	)
);

const streamingOffers = computed(() =>
	filteredOffers.value.filter(offer => ![MonetizationType.Cinema].includes(offer.monetizationType))
);

const spinningTextProps = computed(() => ({
	title: (props.title as TitleDetailSeason).show?.content.title ?? props.title.content.title,
	offers: filteredOffers.value,
	seasonNumber: (props.title as AssertByTypename<TitleDetail, 'Season'>).content?.seasonNumber,
	allRegularOffers: allRegularOffers.value,
}));

const showFreeText = computed(() => {
	const offers = filteredOffers.value.filter(offer => offer.monetizationType !== MonetizationType.Cinema);
	// only if there are offers around, so we won't display "no free offers" without any streaming offers present.
	// this excludes CINEMA offers.
	return offers.length > 0;
});

const isReleased = computed(() => props.title.content.isReleased);

const availableMonetizationTypes = computed(
	() =>
		new Set(
			filteredOffers.value
				.filter(
					offer =>
						![PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
							offer.presentationType
						)
				)
				.map(cur => cur.monetizationType)
		)
);

const dvdBluRayOffers = computed(() => props.title.offersDvdBluray);

const spinningTextComponent = computed(() => {
	if (availableMonetizationTypes.value.has(MonetizationType.Flatrate)) {
		if (availableMonetizationTypes.value.has(MonetizationType.Rent)) {
			if (availableMonetizationTypes.value.has(MonetizationType.Buy)) {
				if (availableMonetizationTypes.value.has(MonetizationType.Ads)) {
					// if ADS, FLAT, RENT, BUY
					return defineAsyncComponent(() => import('@/components/details/spinning-text/AdsFlatRentBuy.vue'));
				} else {
					// if FLAT, RENT, BUY
					return defineAsyncComponent(() => import('@/components/details/spinning-text/FlatRentBuy.vue'));
				}
			} else {
				if (availableMonetizationTypes.value.has(MonetizationType.Ads)) {
					// if FLAT, ADS, RENT
					return defineAsyncComponent(() => import('@/components/details/spinning-text/FlatAdsRent.vue'));
				} else {
					// if FLAT, RENT
					return defineAsyncComponent(() => import('@/components/details/spinning-text/FlatRent.vue'));
				}
			}
		} else if (availableMonetizationTypes.value.has(MonetizationType.Buy)) {
			if (availableMonetizationTypes.value.has(MonetizationType.Ads)) {
				// if FLAT, ADS, BUY
				return defineAsyncComponent(() => import('@/components/details/spinning-text/FlatAdsBuy.vue'));
			} else {
				// if FLAT, BUY
				return defineAsyncComponent(() => import('@/components/details/spinning-text/FlatBuy.vue'));
			}
		} else {
			if (availableMonetizationTypes.value.has(MonetizationType.Ads)) {
				// if FLAT, ADS
				return defineAsyncComponent(() => import('@/components/details/spinning-text/FlatAds.vue'));
			} else {
				// if only FLAT
				return defineAsyncComponent(() => import('@/components/details/spinning-text/Flat.vue'));
			}
		}
	} else if (availableMonetizationTypes.value.has(MonetizationType.Rent)) {
		if (availableMonetizationTypes.value.has(MonetizationType.Buy)) {
			if (availableMonetizationTypes.value.has(MonetizationType.Ads)) {
				// if ADS, BUY, RENT
				return defineAsyncComponent(() => import('@/components/details/spinning-text/AdsBuyRent.vue'));
			} else {
				// if BUY, RENT
				return defineAsyncComponent(() => import('@/components/details/spinning-text/BuyRent.vue'));
			}
		} else {
			if (availableMonetizationTypes.value.has(MonetizationType.Ads)) {
				// if ADS, RENT
				return defineAsyncComponent(() => import('@/components/details/spinning-text/AdsRent.vue'));
			} else {
				// if only RENT
				return defineAsyncComponent(() => import('@/components/details/spinning-text/Rent.vue'));
			}
		}
	} else if (availableMonetizationTypes.value.has(MonetizationType.Buy)) {
		if (availableMonetizationTypes.value.has(MonetizationType.Ads)) {
			// if ADS, BUY
			return defineAsyncComponent(() => import('@/components/details/spinning-text/AdsBuy.vue'));
		} else {
			// if only BUY
			return defineAsyncComponent(() => import('@/components/details/spinning-text/Buy.vue'));
		}
	} else if (availableMonetizationTypes.value.has(MonetizationType.Ads)) {
		// only ADS
		return defineAsyncComponent(() => import('@/components/details/spinning-text/Ads.vue'));
	} else if (availableMonetizationTypes.value.has(MonetizationType.Free)) {
		// only FREE
		return defineAsyncComponent(() => import('@/components/details/spinning-text/Free.vue'));
	} else if (isInternationalOffersActive.value && allRegularOffers.value.length > 0) {
		// international offers
		return defineAsyncComponent(() => import('@/components/details/spinning-text/International.vue'));
	} else if (dvdBluRayOffers.value.length === 0) {
		// no offers
		return defineAsyncComponent(() => import('@/components/details/spinning-text/NoneOffers.vue'));
	}
	return null;
});

//Upcoming spinning texts
const upcomingReleases = computed(() => {
	if (!allowedLanguagesUpcomingReleases.includes(language.value)) return [];

	return props.title.content.upcomingReleases.filter(
		release =>
			!!release.package &&
			!!allProvidersByShortName.value[release.package.shortName as keyof Provider] &&
			!domesticOffers.value.some(
				offer =>
					offer.package.shortName === release?.package?.shortName &&
					offer.monetizationType === release?.package?.monetizationTypes?.[0]
			)
	);
});

const upcomingReleasesGroupedByDateAndType = computed(() => {
	const seen = new Set();
	//group upcoming releases by formatedReleaseDate (date, day, month, season etc..) and by releaseType (theatrical, digital)
	return Object.values(
		upcomingReleases.value
			?.reduce(
				// temporary fix for duplicate upcoming releases - to be handled by BE
				(acc, current, index) => {
					const uniqueKey = current.package
						? `${current.package?.packageId}-${current.package?.monetizationTypes[0]}`
						: index.toString();

					if (!seen.has(uniqueKey)) {
						seen.add(uniqueKey);
						acc.push(current);
					}

					return acc;
				},
				[] as UpcomingRelease[]
			)
			.reduce((acc, item, index) => {
				const releaseDate: string = item.releaseDate
					? formattedReleaseDate(item.releaseDate, item.label, language.value, 'short', country.value, true)
							.heading
					: index.toString();
				const key = `${releaseDate}_${item.releaseType}`;

				if (!acc[key]) {
					acc[key] = [];
				}
				acc[key].push(item as UpcomingRelease);
				return acc;
			}, {} as Record<string, UpcomingRelease[]>)
	);
});

// In the buybox upcoming offers are first grouped by Date&ReleaseType.
// Upcoming releases with same formatted date and releasetype are then joined in a string, provders = 'AppleTv+, Amazon Prime'

const upcomingDigitalReleasesForSpinningTexts = computed(() => {
	return upcomingReleasesGroupedByDateAndType.value
		.filter(group => group[0].releaseType === ReleaseType.Digital)
		.map(group => upcomingReleaseToSpinningText(group));
});

const upcomingTheatricalReleasesForSpinningTexts = computed(() => {
	return upcomingReleasesGroupedByDateAndType.value
		.filter(group => group[0].releaseType === ReleaseType.Theatrical)
		.map(group => upcomingReleaseToSpinningText(group));
});

const hideDigitalSpinningText = computed(
	// hide digital spinning text when there are no digital offers if there are upcoming digital offers
	() =>
		!filteredOffers.value.some(offer => offer.monetizationType !== MonetizationType.Cinema) &&
		upcomingDigitalReleasesForSpinningTexts.value.length > 0
);

const showCinemaText = computed(() => {
	if (!allowedLanguagesUpcomingReleases.includes(language.value)) return false;
	if (availableMonetizationTypes.value.has(MonetizationType.Cinema)) return true;

	return (
		upcomingTheatricalReleasesForSpinningTexts.value.length ||
		props.title.content.upcomingReleases?.some(
			el => el.releaseType === 'THEATRICAL' && (!el.package || ['imd', 'tmd'].includes(el.package.shortName))
		)
	);
});

function upcomingReleaseToSpinningText(group: UpcomingRelease[]) {
	return {
		date: formattedReleaseDate(
			group[0].releaseDate,
			getUpcomingLabelForFormattedDate(group[0].label),
			language.value,
			'long',
			country.value,
			true
		).heading,
		label: group[0].label,
		providers: group
			.filter(el => el.package?.clearName)
			.map(el => el.package?.clearName)
			.join(', '),
	};
}

//Upcoming

return { __sfc: true,DvdBluRay, Cinema, FreeV2, Upcoming, props, country, language, allProvidersByShortName, allRegularOffers, isInternationalOffersActive, AVODOffer, avodExcludedTechnicalNames, domesticOffers, filteredOffers, streamingOffers, spinningTextProps, showFreeText, isReleased, availableMonetizationTypes, dvdBluRayOffers, spinningTextComponent, upcomingReleases, upcomingReleasesGroupedByDateAndType, upcomingDigitalReleasesForSpinningTexts, upcomingTheatricalReleasesForSpinningTexts, hideDigitalSpinningText, showCinemaText, upcomingReleaseToSpinningText, AdUnit, UpcomingSpinningSegment }
}

})